import { useEffect, useMemo, useState } from "react";
import {
  useTable as useTanstackTable,
  type FilterFn,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
} from "@tanstack/react-table";
import { ColumnDef } from "components/table";
import {
  appTableFeatures,
  type AppFeatures,
  type Row,
  type Table,
} from "shared/react-table/appTable";

interface TableProps<T extends object> {
  columns: ColumnDef<T>[];
  data: T[];
  enableRowSelection?: boolean | ((row: Row<T>) => boolean);
  enableSorting?: boolean;
  totalRecords: number;
  rowSelection?: RowSelectionState;
  setRowSelection?: OnChangeFn<RowSelectionState>;
  sorting?: SortingState;
  setSorting?: OnChangeFn<SortingState>;
  getRowId?: (row: T, index: number) => string;
  globalFilterFn?: FilterFn<AppFeatures, T>;
  globalFilter?: string;
  setGlobalFilter?: OnChangeFn<string>;
  enableMultiSort?: boolean;
  sortDescFirst?: boolean;
}

export const useTable = <T extends object>({
  columns,
  data,
  enableRowSelection = false,
  enableSorting = false,
  totalRecords,
  rowSelection,
  setRowSelection,
  sorting,
  setSorting,
  getRowId,
  globalFilterFn,
  globalFilter,
  setGlobalFilter,
  enableMultiSort,
  sortDescFirst,
}: TableProps<T>) => {
  const [localRowSelection, setLocalRowSelection] = useState({});
  const [editing, setEditing] = useState({});
  const [selectedAll, setSelectedAll] = useState(false);
  const [localGlobalFilter, setLocalGlobalFilter] = useState("");

  const listDataMemo = useMemo(() => data || [], [data]);

  const table = useTanstackTable<AppFeatures, T>({
    features: appTableFeatures,
    data: listDataMemo,
    columns,
    state: {
      rowSelection: rowSelection ?? localRowSelection,
      editing,
      selectedAll,
      globalFilter: globalFilter ?? localGlobalFilter,
      ...(sorting ? { sorting } : {}),
    },
    getRowId: getRowId ?? ((_, index) => String(index)),
    manualPagination: true,
    rowCount: totalRecords,
    enableRowSelection,
    enableSorting,
    globalFilterFn,
    onGlobalFilterChange: setGlobalFilter ?? setLocalGlobalFilter,
    enableMultiSort,
    sortDescFirst,
    onRowSelectionChange: setRowSelection ?? setLocalRowSelection,
    ...(setSorting ? { onSortingChange: setSorting } : {}),
    onEditChange: setEditing,
    onSelectedAllChange: setSelectedAll,
  });

  const isAllPageRowsSelected = table.getIsAllPageRowsSelected();

  useEffect(() => {
    if (!isAllPageRowsSelected && selectedAll) {
      // drops the cross-page "all results selected" flag once the page selection breaks; the flag follows table-owned selection state, so there is no event handler to reset it from
      setSelectedAll(false);
    }
  }, [selectedAll, isAllPageRowsSelected]);

  return { table: table as Table<T> };
};
